'use client';

import * as Sentry from '@sentry/nextjs';
import posthog from 'posthog-js';

import type { ClientContext } from '@cineamo/api/models/cart/ClientContext.types';

import { IS_ANALYTICS_ENABLED } from '@cineamo/composables/src/providers/analytics-environment';

import type { BookingActions } from './booking-actions';
import {
    type BookingErrorReport,
    captureBookingError,
    describeBookingError,
    getBookingContext,
    getLastRequestId
} from './booking-telemetry';
import { buildClientContext } from './booking-telemetry-helpers';

export { clearBookingContext, paymentIntentIdOf, setBookingContext } from './booking-telemetry';

let recordingConsent = false;

/** BookingObservability tells us whether the visitor consented to analysis: without it no replay id leaves the browser. */
export function setRecordingConsent(granted: boolean): void {
    recordingConsent = granted;
}

/** What the browser reports with the seat-lock PUT and the PaymentIntent request, see `buildClientContext`. */
function currentClientContext(client: string | undefined): ClientContext {
    try {
        const posthogReady = IS_ANALYTICS_ENABLED && posthog.__loaded;
        return buildClientContext({
            client,
            pathname: window.location.pathname,
            userAgent: navigator.userAgent,
            release: Sentry.getClient()?.getOptions().release,
            requestId: getLastRequestId(),
            consent: recordingConsent,
            posthogSessionId: recordingConsent && posthogReady ? posthog.get_session_id() : undefined,
            sentryReplayId: recordingConsent ? Sentry.getReplay()?.getReplayId() : undefined
        });
    } catch {
        return {};
    }
}

/**
 * The host's booking actions, each carrying the client context as its last argument (CIN-15436). Wrapped once in the
 * CartProvider, so no call site of the seat-lock sync or the PaymentIntent request has to remember it.
 */
export function withClientContext(actions: BookingActions, client: string | undefined): BookingActions {
    return {
        ...actions,
        syncSeatLocks: (cartUuid, showingId, selectedSeats, cartItems) =>
            actions.syncSeatLocks(cartUuid, showingId, selectedSeats, cartItems, currentClientContext(client)),
        createPaymentIntent: (cartUuid, buyer, currency, locale) =>
            actions.createPaymentIntent(cartUuid, buyer, currency, locale, currentClientContext(client))
    };
}

type PendingEvent = { event: string; properties: Record<string, unknown>; timestamp: Date };

/** Captured before `posthog.init` ran: the provider inits in its effect, after the page's own (child) effects. */
const pending: PendingEvent[] = [];
const PENDING_POLL_MS = 250;
// ponytail: polls for init instead of a hook into every host's provider; after this long PostHog is not coming.
const PENDING_GIVE_UP_MS = 30_000;
let pendingTimer: ReturnType<typeof setInterval> | undefined;

function send({ event, properties, timestamp }: PendingEvent): void {
    // Unbatched (CIN-15446): a funnel step is often the last thing before a redirect (PayPal, 3-D Secure) or an
    // app switch, and a batched one waits up to 3 s in the queue. On a page that is unloading PostHog sends it by
    // sendBeacon itself. The timestamp keeps an event held back for init at the moment it happened.
    posthog.capture(event, properties, { send_instantly: true, timestamp });
}

function flushWhenLoaded(): void {
    if (pendingTimer) return;
    const startedAt = Date.now();
    pendingTimer = setInterval(() => {
        if (!posthog.__loaded && Date.now() - startedAt < PENDING_GIVE_UP_MS) return;
        clearInterval(pendingTimer);
        pendingTimer = undefined;
        const events = pending.splice(0);
        if (posthog.__loaded) events.forEach(send);
    }, PENDING_POLL_MS);
}

/**
 * A booking funnel step, to PostHog (with the ids known so far and the last request id) and as a Sentry breadcrumb.
 * Names are `booking_*`, snake case, like the mobile app's: payment_submitted, payment_intent_created,
 * payment_confirmed, redirect_returned, redirect_stalled, order_fulfilled, order_failed, poll_limit_reached, error.
 * PostHog is the destination of the web completion rate. A step before PostHog's init (the page a payment redirect
 * returns to reports on mount) is held until it loaded; without a PostHog client it is a quiet no-op.
 */
export function trackBookingEvent(event: `booking_${string}`, properties: Record<string, unknown> = {}): void {
    try {
        Sentry.addBreadcrumb({ category: 'booking', message: event, data: properties, level: 'info' });
        if (!IS_ANALYTICS_ENABLED) return;
        const all = { ...getBookingContext(), request_id: getLastRequestId(), ...properties };
        const captured = {
            event,
            properties: Object.fromEntries(Object.entries(all).filter(([, value]) => value !== undefined)),
            timestamp: new Date()
        };
        if (posthog.__loaded) {
            send(captured);
            return;
        }
        pending.push(captured);
        flushWhenLoaded();
    } catch {
        // Observability must never fail a booking.
    }
}

/** A failure the booking swallows: Sentry (exception or message) and the PostHog event `booking_error`. */
export function reportBookingError(report: BookingErrorReport): void {
    captureBookingError(report);
    const { status, reason_code: reasonCode } = describeBookingError(report.error) as {
        status?: number;
        reason_code?: string;
    };
    trackBookingEvent('booking_error', {
        step: report.step,
        code: report.code,
        level: report.level ?? 'error',
        status,
        reason_code: reasonCode,
        ...report.context
    });
}

type ConfirmError = { type?: string; code?: string; decline_code?: string; message?: string };

/**
 * The result of `stripe.confirmPayment` (no redirect): `booking_payment_confirmed`, with the outcome. A declined card or
 * an invalid field is the buyer's, so it is counted in PostHog only; every other Stripe error is ours, Sentry too.
 */
export function trackPaymentConfirmed(method: string | null | undefined, error?: ConfirmError): void {
    if (!error) {
        trackBookingEvent('booking_payment_confirmed', { outcome: 'authorized', method });
        return;
    }
    trackBookingEvent('booking_payment_confirmed', {
        outcome: 'error',
        method,
        error_type: error.type,
        error_code: error.code,
        decline_code: error.decline_code
    });
    if (error.type !== 'card_error' && error.type !== 'validation_error') {
        reportBookingError({
            step: 'confirm-payment',
            code: error.type ?? 'unknown',
            error: { message: error.message },
            extra: { code: error.code, method }
        });
    }
}
